import React, { useCallback } from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { useQueryClient } from '@tanstack/react-query';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import {
  MultiDropdown,
  useMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import Api from '../../lib/api';

type Props = {
  apiClient: Api;
  testId: string;
  label: string;
};

const SchoolsDropdown = ({ apiClient, testId, label }: Props) => {
  const { watch, setValue } = useFormContext();
  const queryClient = useQueryClient();

  const selectedSchools = watch('schools');

  const fetchItems = useCallback(
    async (queryKey: string[], fetchQuery: () => Promise<Item[]>) => {
      const cachedData: Item[] | null | undefined =
        queryClient.getQueryData(queryKey);
      if (cachedData) {
        return { items: cachedData };
      }

      const items = await queryClient.fetchQuery({
        queryKey,
        queryFn: () => fetchQuery(),
      });

      return { items };
    },
    [queryClient]
  );

  const fetchSchools = useCallback(
    async (term: string) => {
      return await fetchItems(['schools', term], () =>
        apiClient.fetchSchools(term)
      );
    },
    [apiClient, fetchItems]
  );

  const schoolDropdownProps = useMultiDropdown({
    fetchItems: fetchSchools,
    selectedItems: selectedSchools,
    onSelectedItemsChange: (e) =>
      setValue('schools', e.selectedItems, { shouldDirty: true }),
    preventLoadOnMount: true,
  });

  return (
    <MultiDropdown dropdownProps={schoolDropdownProps}>
      <MultiDropdown.Field data-provides={testId} label={label} />
      <MultiDropdown.Flyout />
    </MultiDropdown>
  );
};

export default SchoolsDropdown;
